import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

/* ─── Google Font: Instrument Serif (display) + DM Sans (body) ───
   Matches HelpDisputePage's design language. ─── */
const FONTS_URL =
  "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap";

export function useFonts() {
  useEffect(() => {
    if (document.querySelector('link[href*="Instrument+Serif"]')) return;
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = FONTS_URL;
    document.head.appendChild(l);
  }, []);
}

/* ─── Shared design tokens (matches HelpDisputePage) ─── */
export const T = {
  g700: "#155c36", g600: "#1b7a47", g500: "#1e9254", g200: "#a7d9bc", g100: "#d6f0e3", g50: "#eef8f3",
  n900: "#0f1117", n800: "#1c2130", n700: "#2d3549", n600: "#4a5268", n400: "#8a93a8",
  n200: "#d1d5e0", n150: "#e4e7ef", n100: "#eef0f5", n50: "#f7f8fa", white: "#ffffff",
  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

/* ─── Inline icons (subset, mirrors HelpDisputePage's Icon component) ─── */
export const LegalIcon = ({ name, size = 16, color = "currentColor", strokeWidth = 1.5 }) => {
  const paths = {
    ArrowLeft: <><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></>,
    Mail: <><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></>,
    ShieldCheck: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></>,
  };
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
};

/* ─── Page shell shared by Terms and Privacy ─── */
export function LegalPageShell({ badge, title, titleEm, lastUpdated, children }) {
  useFonts();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState(null);

  return (
    <div style={{ fontFamily: T.sans, background: T.white, minHeight: "100vh", color: T.n900 }}>
      <style>{`
        * { box-sizing: border-box; }
        .jrn-legal-link { color: ${T.g600}; text-decoration: none; border-bottom: 1px solid ${T.g200}; transition: border-color .15s, color .15s; }
        .jrn-legal-link:hover { color: ${T.g700}; border-color: ${T.g600}; }
        .jrn-toc-link { display: block; padding: 7px 0; font-size: 13px; color: ${T.n400}; text-decoration: none; transition: color .15s; border-left: 2px solid transparent; padding-left: 12px; }
        .jrn-toc-link:hover { color: ${T.n800}; }
        .jrn-toc-link.active { color: ${T.g600}; border-left-color: ${T.g500}; font-weight: 600; }
      `}</style>

      {/* ══ HERO ══ */}
      <div style={{ padding: "clamp(48px,6vw,80px) 20px clamp(40px,5vw,56px)", borderBottom: `1px solid ${T.n100}` }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <button onClick={() => navigate(-1)} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "none", border: "none", cursor: "pointer",
            color: T.n400, fontSize: 13, fontFamily: T.sans,
            padding: 0, marginBottom: 28, transition: "color .15s",
          }}
            onMouseEnter={e => e.currentTarget.style.color = T.n800}
            onMouseLeave={e => e.currentTarget.style.color = T.n400}>
            <LegalIcon name="ArrowLeft" size={14} strokeWidth={2} />
            Back
          </button>

          <div style={{ display: "inline-flex", alignItems: "center", gap: 7, color: T.g600, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 18 }}>
            <LegalIcon name="ShieldCheck" size={14} color={T.g500} strokeWidth={2} />
            {badge}
          </div>

          <h1 style={{ fontFamily: T.serif, fontSize: "clamp(36px, 6vw, 56px)", fontWeight: 400, margin: "0 0 12px", lineHeight: 1.1, color: T.n900, letterSpacing: "-0.03em" }}>
            {title} <em style={{ fontStyle: "italic", color: T.g600 }}>{titleEm}</em>
          </h1>

          <p style={{ fontSize: 14, color: T.n400, margin: 0 }}>Last updated {lastUpdated}</p>
        </div>
      </div>

      {/* ══ CONTENT ══ */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "clamp(40px,6vw,64px) 20px clamp(72px,9vw,120px)" }}>
        {children}

        {/* ── Footer ── */}
        <div style={{ borderTop: `1px solid ${T.n100}`, marginTop: 56, paddingTop: 40, textAlign: "center" }}>
          <p style={{ fontFamily: T.serif, fontSize: "clamp(20px,3vw,26px)", fontWeight: 400, color: T.n900, margin: "0 0 10px", letterSpacing: "-0.02em" }}>
            Questions about this policy?
          </p>
          <p style={{ fontSize: 14, color: T.n400, margin: "0 0 24px", lineHeight: 1.65 }}>
            Our team is happy to help clarify anything.
          </p>
          <a href="mailto:legal@jiraniapp.com" style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: T.g500, color: T.white, border: "none",
            padding: "12px 24px", borderRadius: 10, fontSize: 14,
            fontWeight: 600, textDecoration: "none", fontFamily: T.sans,
            letterSpacing: "0.01em",
          }}>
            <LegalIcon name="Mail" size={15} color={T.white} strokeWidth={1.8} />
            Contact legal@jiraniapp.com
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── Section block ─── */
export function LegalSection({ id, num, title, children }) {
  return (
    <section id={id} style={{ marginBottom: 40, scrollMarginTop: 24 }}>
      <h2 style={{
        fontFamily: T.serif, fontSize: "clamp(22px,3.5vw,28px)", fontWeight: 400,
        margin: "0 0 16px", color: T.n900, letterSpacing: "-0.02em", lineHeight: 1.25,
        display: "flex", alignItems: "baseline", gap: 12,
      }}>
        <span style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 600, color: T.g500, flexShrink: 0 }}>{num}</span>
        {title}
      </h2>
      <div style={{ fontSize: 15, color: T.n600, lineHeight: 1.8 }}>
        {children}
      </div>
    </section>
  );
}

/* ─── Paragraph ─── */
export function LegalP({ children }) {
  return <p style={{ margin: "0 0 14px" }}>{children}</p>;
}

/* ─── Bullet list ─── */
export function LegalList({ items }) {
  return (
    <ul style={{ margin: "0 0 14px", paddingLeft: 22, listStyle: "none" }}>
      {items.map((item, i) => (
        <li key={i} style={{ position: "relative", marginBottom: 8, paddingLeft: 4 }}>
          <span style={{ position: "absolute", left: -18, top: "0.7em", width: 5, height: 5, borderRadius: "50%", background: T.g200 }} />
          {item}
        </li>
      ))}
    </ul>
  );
}

/* ─── Sub-heading within a section ─── */
export function LegalSubhead({ children }) {
  return (
    <h3 style={{ fontSize: 14, fontWeight: 600, color: T.n800, margin: "20px 0 8px", letterSpacing: "-0.01em" }}>
      {children}
    </h3>
  );
}